import React, { useState } from 'react';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import cx from 'classnames';

import AddTextAssetModal from '~/App/Components/Modals/AddTextAssetModal';
import { useQueryClient } from 'react-query';
import { useCreateUserAsset, userAssetsQueryKeys } from '../Api/UserAsset';
import { useLibraryTabs } from '../hooks/use-library-tabs';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  TESTIMONIAL_LIBRARY_ENTER_TEXT,
  TESTIMONIAL_LIBRARY_TEXT_ADDED,
} from '~/generated/AnalyticsTypes';
import { RichText } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';

export interface EnterWrittenTextButtonProps {
  text?: string;
  variant?: ButtonVariant;
  icon?: React.ReactNode;
  className?: string;
  onSuccess?: () => void;
}

export default function EnterWrittenTextButton({
  text = 'Enter written text',
  variant = ButtonVariant.Secondary,
  icon,
  className,
  onSuccess,
}: EnterWrittenTextButtonProps) {
  const [showAddTextAssetModal, setShowAddTextAssetModal] = useState(false);
  const [content, setContent] = useState<RichText>();
  const queryClient = useQueryClient();
  const { activeTabName } = useLibraryTabs();
  const analytics = useAnalytics();

  const closeAddTextAssetModal = () => {
    setContent(undefined);
    setShowAddTextAssetModal(false);
  };

  const { mutateAsync: createUserAsset, isLoading: isCreating } =
    useCreateUserAsset({
      onSuccess: () => {
        void queryClient.invalidateQueries(userAssetsQueryKeys.lists());
        closeAddTextAssetModal();
        onSuccess?.();
      },
    });

  const handleOnCreate = () => {
    void createUserAsset({ content_rich: content });
    analytics.track(TESTIMONIAL_LIBRARY_TEXT_ADDED, {
      context: activeTabName,
    });
  };

  const handleOnClick = () => {
    analytics.track(TESTIMONIAL_LIBRARY_ENTER_TEXT, {
      context: activeTabName,
    });
    setShowAddTextAssetModal(true);
  };

  const buttonContent = (
    <span className='flex items-center justify-center gap-3'>
      {icon ?? <i className='icon icon-sm icon-enter-text' />}
      {text}
    </span>
  );

  return (
    <>
      <Button
        className={cx(className, 'h-10')}
        variant={variant}
        onClick={handleOnClick}
      >
        {buttonContent}
      </Button>
      <AddTextAssetModal
        heading='Enter written text'
        placeholder='Enter notes or written testimonial'
        content={content}
        onSetContent={setContent}
        open={showAddTextAssetModal}
        onRequestClose={closeAddTextAssetModal}
        loading={isCreating}
        onSave={handleOnCreate}
      />
    </>
  );
}
